import React from 'react'

import { memo, useMemo, useCallback, useEffect, useRef, useState } from 'react'

import { v4 as uniqid } from 'uuid'

import { HiOutlineExclamation } from 'react-icons/hi'

import arePropsEqual from '@function/arePropsEqual'
import bemit         from '@function/bemit'

import FormsStore from '@Store/Forms'

import Loader from '@Pure/Loader'

/**
 * @param { Object } props
 * @param { function } props.onChange
 * @param { function } props.register react-form-hook register
 * @param { String } [props.label]
 * @param { String } props.name
 * @param { String|Boolean } [props.error]
 *
 * @returns { React.ReactElement }
**/
export default memo(function Select ({ label, onChange, name, ...props })
{
	const { removeError } = props;

	const is__readOnly = props['read-only'] ? true : false;

	const ref = useRef();
	const [show_error, setShowError] = useState();

	const [id, setId] = useState(uniqid());

	const type = typeof onChange === 'function'
	? 'legacy'
	: 'pullstate';

	const FORM_NAME = props.form || 'no-form';

	const _submit_id = FormsStore.useState(s => s[FORM_NAME]?._submit_id);

	const errors_version_ref = useRef();

	const state_value = FormsStore.useState(s => s[FORM_NAME]?.[name]);

	const default_value = props['default-value'] || props.options?.[0]?.value;

	useEffect(function ()
	{
	// réaffiche les erreurs au moment de submit le formulaire,
	// pour gérer le cas où le champ a été modifié (donc l'erreur masquée)
	// mais la validation retourne la même erreur qu'avant modification
		if (_submit_id && _submit_id !== errors_version_ref.current)
		{
			setShowError(true);

			errors_version_ref.current = _submit_id;

			return;
		}

		setShowError(function (show_error)
		{
			return !props.error & show_error === undefined && !_submit_id
			? show_error
			: !props.error && !show_error
			? show_error
			: props.error && show_error
			? show_error
			: props.error ? true : false;
		});
	},
	[props.error, _submit_id]);

	useEffect(() =>
	{
		if (type !== 'pullstate') return;

	// pullstate initialisé ou reset, on l'update avec la valeur par défaut
		if (state_value === undefined || (state_value === '' && default_value))
		{
			FormsStore.update(function (state)
			{
				state[FORM_NAME] = state[FORM_NAME] || { };

				state[FORM_NAME][name] = default_value !== undefined
				? default_value
				: '';
			});

			return;
		}

		if (ref.current.value !== state_value)
		{
			ref.current.value = state_value; // sync de la valeur de du select
		}
	},
	[type, state_value, default_value, FORM_NAME, name]);

	const options = useMemo(function ()
	{
		return props.options.map(function (opt)
		{
			const value = opt.value !== undefined ? opt.value : opt.label || '';
			const label = opt.label !== undefined ? opt.label : opt.value || '';

			return (
				<option key={ value } value={ value }>{ label }</option>
			);
		});
	},
	[props.options]);

	const handleChange = useCallback(function (e)
	{
		if (props.error && typeof removeError === 'function')
		{
			removeError(name);
		}

		const value = e.target?.value || '';

		FormsStore.update(function (state)
		{
			state[FORM_NAME] = state[FORM_NAME] || { };

			state[FORM_NAME][name] = value;
		});
	},
	[props.error, removeError, name, FORM_NAME]);

	const modifiers = useMemo(function ()
	{
		const modifiers = [];

		if (show_error && props.error) modifiers.push('error');
		if (is__readOnly) modifiers.push('disabled');
		if (props.required) modifiers.push('required');

		return modifiers;
	},
	[props.error, is__readOnly, props.required, show_error]);

	let class_name = bemit('c-select', modifiers.concat(props.bemit || []));

	if (props.className) class_name += ' ' + props.className;

	return (
		<div className={ class_name }>
			<div className="c-select__select-wrapper">
				<select className="c-select__select" id={ id }
					onChange={ type === 'legacy' ? onChange : handleChange }
					value={ type === 'legacy' ? props.value : undefined }
					disabled={ props['read-only'] === true }
					ref={ ref }
					name={ name }>
					{ options }
				</select>
			</div>

			{
				label ?
				(
					<label htmlFor={ id } className="c-select__label">
						{ label }
					</label>
				)
				: undefined
			}

			{
				show_error && props.error ?
				(
					<span className={ bemit('c-field-error', ['select']) }>
						<HiOutlineExclamation className="c-field-error__icon" />
						{ props.error }
					</span>
				)
				: undefined
			}

			{
				props.loading === true ?
				(
					<Loader className="c-loader"
						bemit={ ['primary', 'bg-semi-white'] }
					/>
				)
				: undefined
			}

		</div>
	);
},
arePropsEqual);